import { IResource } from 'src/services/integration/integrations.types';

import { PlaceType } from 'src/components/hook-form/rhf-maps-autocomplete';

import { TenantType } from 'src/types/enums';

// eslint-disable-next-line import/no-cycle
import { Category } from '../utils/utils.types';
import { Organization, ProfileOrganization } from '../auth/auth.types';
import { SortConfig, PageOptions, PaginatedResponse } from '../api.types';
import { UpdateCustomFieldValueDto } from '../custom-fields/custom-field.types';
import { User, Department, CompanySizeRange } from '../organisation/organisation.types';
import { SubmissionData, ApplicationStage, RejectionContext } from '../applications/applications.types';
import {
  RequestStatus,
  ICompanyProfile,
  CompanyProfileType,
} from '../connections/connections.types';

export type JobsResponse = PaginatedResponse<IJob>;

export type JobFilters = {
  search_query?: string;
  job_status?: JobStatus;
  assigned_account?: string;
  page: number;
  per_page: number;
  verification_status?: JobVerificationStatus;
  application_status?: ApplicationStage;
  linked_application?: boolean;
  role_status?: RoleStatus;
};

export type GetJobsRequestProps = JobFilters & Partial<SortConfig>;

export type GetJobBoardRequestProps = {
  board_id: string;
  page: number;
  per_page: number;
  search_query?: string;
  job_type?: JobType;
  employment_type?: EmploymentType;
  work_style?: WorkStyleType;
  industry?: string;
};

export enum ApplyFlowType {
  FORM_APPLY = 'form_apply',
  ACCOUNT_APPLY = 'account_apply'
}

export enum ConnectFlowType {
  FORM_CONNECT = 'form_connect',
  ACCOUNT_CONNECT = 'account_connect',
}

export type ApplyFlowFormData = {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  country_code?: string;
  resume_id: string;
  cover_letter_id?: string;
};

export type ApplyFlowAccountData = {
  account_id: string;
  resume_id: string;
  cover_letter_id?: string;
};

export type InitiateApplyFlowRequest = {
  board_id: string;
  job_id: string;
  flow_type: ApplyFlowType;
  form_data?: ApplyFlowFormData;
  account_data?: ApplyFlowAccountData;
  compliance_submissions?: SubmissionData[];
  source?: string;
};

export type ConnectFlowFormData = {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  country_code?: string;
  resume_id: string;
};

export type ConnectFlowAccountData = {
  account_id: string;
  resume_id: string;
};

export type InitiateConnectIntentRequest = {
  board_id: string;
  flow_type: ConnectFlowType;
  form_data?: ConnectFlowFormData;
  account_data?: ConnectFlowAccountData;
  list_ids?: string[];
  // JT1-1276 (Phase 6): reCAPTCHA token from the public Register-with-us form
  recaptcha_token?: string;
};

export enum ApplyResultType {
  VERIFICATION_PENDING = 'verification_pending',
  APPLIED = 'applied',
}

export enum ConnectResultType {
  VERIFICATION_PENDING = 'verification_pending',
  INVITED = 'invited'
}

export enum JobQuestionType {
  TEXT = 'text'
}

export interface IApplyResultResponse {
  result_type: ApplyResultType;
  job_application_id?: string;
  candidate_job_id?: string;
}

export interface IConnectResultResponse {
  result_type: ConnectResultType;
}

export interface IApplyIntentResponse {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  job: {
    id: string;
    title: string;
  }
}

export interface IConnectIntentResponse {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  target: {
    id: string;
    company_name: string;
  }
}

export interface IMinimalJobBoard {
  id: string;
  organization: ProfileOrganization;
  theme_settings?: {
    primary_color?: string;
    secondary_color?: string;
  }
}

export interface IJobBoard {
  id: string;
  embed_code: string;
  link: string;
  organization: ProfileOrganization;
}

export interface IJobQuestion {
  id: string;
  question: {
    text?: string;
  };
  type: JobQuestionType;
  required: boolean;
  order: number;
}

export enum AgencyLinkStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export interface ISharedJob {
  id: string;
  title: string;
  description: string;
  excerpt: string;
}

export interface IJob {
  id: string;
  title: string;
  description: string;
  excerpt: string;
  latest_application_status: ApplicationStage;
  work_style?: WorkStyleType;
  job_type?: JobType;
  employment_type?: EmploymentType;
  recruitment_type?: JobRecruitmentType;
  start_date?: string;
  duration_period?: PeriodType;
  duration_value?: number;
  minimum_salary?: number;
  maximum_salary?: 120000;
  rate_period?: PeriodType;
  pay_rate?: number;
  charge_rate?: number;
  fee_type?: FeeType;
  fee_value?: number;
  estimated_spend_per_vacancy?: number;
  estimated_revenue_per_vacancy?: number;
  fee_percentage?: number;
  shortlist_candidate_limit: number | null;
  currency?: {
    id: string;
    name: string;
    code: string;
  };
  vacancies?: number;
  reference?: string;
  job_board_code?: string;
  role_status?: RoleStatus;
  // Set once someone records why the job closed (JT1-1800); archiving asks only while it is empty
  close_reason_id?: string | null;
  closed_at?: string | null;
  client_company_name?: string;
  client_id?: string;
  client_contact_email?: string;
  client_contact_name?: string;
  recruiter_contact_email?: string;
  recruiter_contact_name?: string;
  recruiter_company_name?: string;
  industry?: Category;
  workplace_locations?: PlaceType[];
  required_skills?: Skill[];
  assigned_account?: Partial<User>;
  client?: {
    id: string;
    type: TenantType;
    company_name: string;
  };
  client_profile: ICompanyProfile;
  recruiter_profile: ICompanyProfile;
  verification_status?: JobVerificationStatus;
  status?: JobStatus;
  sections: JobSection[];
  created_at: Date;
  thread: {
    id: string;
  };
  source_type: SourceType;
  source_url: string;
  apply_url: string;
  excitement_rating: number;
  ai_screening_prompt?: string;
  documents_available: boolean;
  comments_available: boolean;
  candidate_count?: number;
  published_job_boards?: number;
  shortlist_deadline_at: Date;
  integrations: IResource[];
  application?: {
    id?: string;
    stage: ApplicationStage;
    rejection_context?: RejectionContext;
    created_at: Date;
  };
  departments?: Department[];
  compliance_questions?: IJobQuestion[];
  update_request_notes?: string;
  updated_at: Date;
}

export type JobSummary = {
  documents: number;
  applications: {
    count: number;
    candidates: number;
    applicants: number;
    stages: {
      [key: string]: number;
    };
  };
  stakeholders: number;
  placements: number;
  shortlist_deadline_at?: Date;
  shortlist_candidate_limit?: number;
  vacancies?: number;
  role_status?: RoleStatus;
  invites: {
    pending: number;
  };
  offers?: {
    count: number;
    status: {
      [key: string]: number;
    };
  };
  integrations?: number;
};

export interface LinkedAgency {
  id: string;
  company_name: string;
  company_type: TenantType;
  company_information: string;
  organization?: Pick<Organization, 'id' | 'status' | 'type'>;
  type: CompanyProfileType;
  company_size?: CompanySizeRange;
  company_logo?: {
    id: string;
    public_path: string;
  } | null;
  submitted_count: number;
  shortlist_candidate_limit: number | null;
  fee_type: FeeType;
  fee_value: number;
  link_status: AgencyLinkStatus;
}

export interface CreateJobDTO {
  id?: string;
  title: string;
  description: string;
  excerpt?: string;
  default_application_status?: ApplicationStage;
  work_style?: WorkStyleType;
  job_type?: JobType;
  employment_type?: EmploymentType;
  recruitment_type?: JobRecruitmentType;
  start_date?: string;
  duration_period?: PeriodType;
  duration_value?: number;
  minimum_salary?: number;
  maximum_salary?: number;
  rate_period?: PeriodType;
  pay_rate?: number;
  charge_rate?: number;
  fee_type?: FeeType;
  fee_value?: number;
  estimated_revenue_per_vacancy?: number;
  estimated_spend_per_vacancy?: number;
  fee_percentage?: number;
  currency_code?: string;
  vacancies?: number;
  reference?: string;
  role_status?: RoleStatus;
  // sent with role_status closed when editing a job (JT1-1800)
  close_reason_id?: string;
  shortlist_deadline_at?: Date;
  industry_id?: string;
  locations?: PlaceType[];
  skills?: Partial<Skill>[];
  assigned_account?: string;
  client_id?: string;
  client_contact_email?: string;
  client_contact_name?: string;
  client_company_name?: string;
  recruiter_contact_email?: string;
  recruiter_contact_name?: string;
  recruiter_company_name?: string;
  verification_status?: JobVerificationStatus;
  compliance_questions?: {
    type: JobQuestionType;
    text?: string;
    required: boolean;
    order: number;
  }[];
  status?: JobStatus;
  sections?: JobSection[];
  submit_behaviour: string;
  source_url?: string;
  excitement_rating?: number;
  ai_screening_prompt?: string;
  custom_fields?: UpdateCustomFieldValueDto;
  departments?: string[];
}

export type UpdateRoleDTO = {
  status: RoleStatus;
  vacancies: number;
  shortlist_deadline_at: string | null;
};

export type UpdateRoleRequestProps = {
  jobId: string;
  data: UpdateRoleDTO;
};

export enum JobRecruitmentType {
  CONTINGENT = 'contingent',
  EXCLUSIVE = 'exclusive',
  INTERNAL = 'internal',
}

export enum JobStatus {
  DRAFT = 'draft',
  UNAPPROVED = 'unapproved',
  ACTIVE = 'active',
  ARCHIVED = 'archived',
  CHANGES_REQUESTED = 'changes_requested',
}

export enum SubmitBehaviour {
  SUBMIT = 'submit',
  SAVE_AS_DRAFT = 'save_as_draft',
  SAVE = 'save',
}

export enum JobVerificationStatus {
  VERIFIED = 'verified',
  UNVERIFIED = 'unverified',
  PENDING = 'pending',
}

export enum JobBoardAvailabilityStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  UNPUBLISHED = 'unpublished'
}

export enum JobBoardType {
  DEFAULT = 'default',
  IDIBU = 'idibu',
}

export enum EmploymentType {
  PERMANENT = 'permanent',
  FIXED_TERM = 'fixed_term',
  CONTRACT = 'contract',
}

export enum FeeType {
  PERCENTAGE = 'percentage',
  FIXED = 'fixed',
}

export enum FeeSettingsSourceType {
  DEFAULT = 'default',
  CUSTOM = 'custom',
}

export enum SourceType {
  DASHBOARD = 'dashboard',
  CLONE = 'clone',
  JOB_ADDER = 'job_adder',
}

export enum RoleStatus {
  LIVE = 'live',
  ON_HOLD = 'on_hold',
  CLOSED = 'closed',
}

export enum WorkStyleType {
  Hybrid = 'hybrid',
  Remote = 'remote',
  Onsite = 'onsite',
}

export enum JobType {
  FULL_TIME = 'full_time',
  PART_TIME = 'part_time',
  CASUAL = 'casual',
}

export enum PeriodType {
  HOUR = 'hour',
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
}

export enum PaymentFrequency {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  FORTNIGHTLY = 'fortnightly',
  MONTHLY = 'monthly'
}

export type Skill = {
  id?: string;
  name: string;
  creator_type?: SkillCreatorType;
  /** Null until classified; reads as a plain skill (JT1-1692). */
  type?: SkillType | null;
};

export enum SkillType {
  SKILL = 'skill',
  SYSTEM = 'system',
  METHODOLOGY = 'methodology',
  CERTIFICATION = 'certification',
}

export enum SkillCreatorType {
  SYSTEM = 'system',
  TENANT = 'tenant',
}

export interface JobSection {
  type: JobSectionType;
  visibility_rule: VisibilityRule;
}

export enum JobSectionType {
  TITLE = 'title',
  DESCRIPTION = 'description',
  EXCERPT = 'excerpt',
  RECRUITMENT_TYPE = 'recruitment_type',
  WORK_STYLE = 'work_style',
  EMPLOYMENT_TYPE = 'employment_type',
  JOB_TYPE = 'job_type',
  INDUSTRY = 'industry',

  LOCATIONS = 'locations',
  SKILLS = 'skills',
  DOCUMENTS = 'documents',

  DATE_INFORMATION = 'date_information',

  COMPENSATION = 'compensation',

  RECRUITER_FEES = 'recruiter_fees',

  CLIENT = 'client',
  RECRUITER = 'recruiter',
}

export enum VisibilityRule {
  PUBLIC = 'public',
  RESTRICTED = 'restricted',
  HIDDEN = 'hidden',
}

export enum FeeTypeDisplay {
  Fixed = '$',
  Percentage = '%',
}

export interface LinkFeeSettingsDto {
  source_type: FeeSettingsSourceType;
  custom_fees?: {
    fee_value: number;
    fee_type: FeeType;
  };
}

export interface LinkSettingsDto {
  shortlist_candidate_limit: number | null;
  fee_value: number;
  fee_type: FeeType;
}

export interface RequestJobLink {
  job_id: string;
  data: {
    target_id: string;
    notes?: string | null;
    fee_settings?: LinkFeeSettingsDto;
    shortlist_candidate_limit?: number | null;
  };
}

export interface JobLinkRequest {
  id: string;
  source_job: IJob;
  status: JobLinkStatus;
  source_organization?: ICompanyProfile;
  target_organization?: ICompanyProfile;
  created_at: Date;
}

export interface UpdateJobBoardAvailabilityDto {
  job_id: string;
  status: JobBoardAvailabilityStatus;
}

export type GetJobLinkRequestsProps = PageOptions & {
  status: JobLinkStatus;
};

export enum JobLinkStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
}

export enum JobLinkAction {
  ACCEPT = 'accept',
  REJECT = 'reject',
}

export enum JobInviteAction {
  ACCEPT = 'accept',
  REJECT = 'reject',
}

export enum ImportWorkflowType {
  LINK = 'link',
  CONTENT = 'content',
}

export enum StakeholderLabel {
  HIRING_MANAGER = 'hiring_manager',
  DECISION_MAKER = 'decision_maker',
  INTERNAL_TALENT = 'internal_talent',
  EXECUTIVE = 'executive',
  ADVISOR = 'advisor',
  LEGAL = 'legal',
  HR = 'hr',

  OBSERVER = 'observer',

  CONSULTANT = 'consultant',
  ACCOUNT_MANAGER = 'account_manager',
  TEAM_MANAGER = 'team_manager',
  RESOURCER = 'resourcer',
  ADMINISTRATION = 'administration',

  CANDIDATE = 'candidate',
}

export interface JobLinkActionRequestProps {
  requestId: string;
  data: JobLinkActionData;
}

export interface JobInviteActionRequestProps {
  requestId: string;
  data: JobInviteActionData;
}

export interface ImportJobProps {
  data: {
    workflow_type: ImportWorkflowType;
    url?: string | null;
    content?: string | null;
  };
}

export interface JobInviteActionData {
  action: JobInviteAction;
  link_settings?: {
    job_id?: string;
    link_behaviour: LinkBehaviour;
  };
  compliance_submissions?: SubmissionData[];
  documents?: {
    resume_id?: string;
    additional_documents?: string[];
  }
}

export enum ConnectedCompanyType {
  CUSTOM = 'custom',
  LINKED = 'linked',
  UNLINKED = 'unlinked'
}

export type ConnectedCompanyOption = {
  key: string;
  value: string;
  type: ConnectedCompanyType;
};

export interface JobLinkActionData {
  link_settings?: {
    job_id?: string;
    link_behaviour: LinkBehaviour;
    create_params?: CreateJobDTO;
    shortlist_candidate_limit?: number | null;
  };
  fee_settings?: LinkFeeSettingsDto;
}

export enum LinkBehaviour {
  CLONE_SOURCE = 'clone_source',
  LINK_EXISTING = 'link_existing',
  CREATE_AND_LINK = 'create_and_link',
}

export type GetLinkedAgenciesProps = {
  pageOptions: PageOptions;
  jobId: string;
};

export type UpdateApplicationStatusProps = {
  jobId: string;
  status: ApplicationStage;
};
export type SendJobInviteProps = {
  jobId: string;
  data: {
    email: string;
    first_name: string;
    last_name: string;
  };
};

export type JobInvite = {
  id: string;
  status: RequestStatus;
  created_at: string;
  job: Partial<IJob>;
  initiator: ICompanyProfile;
  first_name: string;
  last_name: string;
  email: string;
};

export type JobBoardAvailability = {
  id: string;
  type: JobBoardType;
  status: JobBoardAvailabilityStatus;
  /** The destination the organization has configured. One of the `links` values below. */
  link?: string;
  /** Every destination this job can be linked at. Website entries are null until a careers URL is set. */
  links?: {
    hosted: string;
    website_query: string | null;
    website_path: string | null;
  };
};

export type JobImportResponse = {
  job_id: string;
};

export type JobChecklistItem = {
  id: string;
  title: string;
  description: string;
  action_url: string;
  checked: boolean;
};

export type JobChecklistResponse = {
  id: string;
  items: JobChecklistItem[];
};

export type UploadJobDocumentProps = {
  jobId: string;
  document_id: string;
};

// stakeholder types

export interface StakeholderLabelResponse {
  id: string;
  label: string;
}

export type JobStakeholderResponse = {
  account: User;
  labels: StakeholderLabelResponse[];
  visibility: VisibilityRule;
};

export interface AssignStakeholderRequestProps {
  job_id: string;
  account_id: string;
  labels: StakeholderLabel[];
  visibility: VisibilityRule;
}

export interface ShareJobDocumentRequestProps {
  document_id: string;
  target_type: TenantType;
  application_id?: string;
}

export interface UpdateStakeholderRequestProps {
  job_id: string;
  account_id: string;
  labels: StakeholderLabel[];
  visibility: VisibilityRule;
}

export interface RemoveStakeholderRequestProps {
  job_id: string;
  account_id: string;
}

export interface AddToJobRequestProps {
  job_id: string;
  candidate_id: string;
  create_invite: boolean;
  default_stage?: ApplicationStage;
  // Document ids the recruiter chose to attach to the application (JT1-1524 follow-up). Omitted =
  // attach all eligible documents (unchanged); an empty array attaches none.
  document_ids?: string[];
  // Why the candidate was added (JT1-1738). Saved as a note on the new application; omit for none.
  note?: AddToJobNote;
}

export interface AddToJobNote {
  text: string;
  /** Mentioned teammates; they are notified like any other note mention. */
  tagged_accounts?: { id: string }[];
}

export type GetJobStakeholdersRequestProps = {
  job_id: string;
  page: number;
  per_page: number;
  search_query?: string;
};

export const ClientStakeholderLabels = [
  StakeholderLabel.HIRING_MANAGER,
  StakeholderLabel.DECISION_MAKER,
  StakeholderLabel.INTERNAL_TALENT,
  StakeholderLabel.EXECUTIVE,
  StakeholderLabel.ADVISOR,
  StakeholderLabel.LEGAL,
  StakeholderLabel.HR,
  StakeholderLabel.OBSERVER,
];

export const RecruiterStakeholderLabels = [
  StakeholderLabel.CONSULTANT,
  StakeholderLabel.ACCOUNT_MANAGER,
  StakeholderLabel.TEAM_MANAGER,
  StakeholderLabel.RESOURCER,
  StakeholderLabel.ADMINISTRATION,
  StakeholderLabel.OBSERVER,
];

export const CandidateStakeholderLabels = [StakeholderLabel.CANDIDATE];
